// Accountant HQ (PRD 40 §5.2).
//
// Uses the plain api helpers rather than the business* ones: these two calls
// precede having a firm in context, so they send no tenant header. When a
// firm-SCOPED screen arrives (the roster, the client book), it needs an
// `X-Firm-Id` helper of its own — deliberately not reusing the business one,
// because a call that accidentally sent the wrong header would be a
// cross-tenant read.

import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm, apiPut } from '../utils/apiUtils';
import { dispositionFilename } from '../utils/contentDisposition';
import { parseData } from './businessApiCommon';
import { BusinessApiError } from './businessApiCommon';
import {
  BillingArrangement,
  BillingPortalFlow,
  CreateClientBusinessInput,
  CreatedClientBusiness,
  ClientContactInput,
  ClientCoverage,
  ClientType,
  CoverageWindow,
  CreatedFirm,
  CreateFirmInput,
  FirmBillingResponse,
  FirmClientModulesResponse,
  FirmBillingTransfer,
  FirmBillingTransferDecision,
  FirmClientDetail,
  FirmEngagementInvitation,
  FirmSettingsInput,
  MigrationBoard,
  MigrationRow,
  RecordClientInput,
  RecordedClient,
  FirmRole,
  FirmStaffAccessInput,
  FirmStaffAssignments,
  FirmStaffInvitation,
  FirmStaffMember,
  FirmStaffResponse,
  FirmSummary,
  LinkableUser,
  StaffAccessReport,
  WorklistClient,
  WorklistFilter,
  WorklistResponse,
} from '../types/firmApi';

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

const BASE = '/api/v1/firm/firms';

/**
 * The branding fields, defaulted at the boundary.
 *
 * They are REQUIRED on `FirmSummary` because the server always sends them —
 * but these types are hand-written, and this one is read by a screen that
 * calls `.toUpperCase()` on the colour. An API that has not shipped them yet
 * would otherwise take Firm info down with a TypeError rather than showing a
 * green swatch, so the default lives here, at the one seam where a response
 * becomes a `FirmSummary`, and not in every component that reads one.
 */
const DEFAULT_ACCENT = '#1B4332';

const withBranding = <T extends FirmSummary>(firm: T): T => ({
  ...firm,
  accent_color: firm.accent_color ?? DEFAULT_ACCENT,
  logo_url: firm.logo_url ?? null,
});

export const firmService = {
  /** GET /firm/firms — every firm I belong to, with my role in each. */
  async listFirms(): Promise<FirmSummary[]> {
    const response = await apiGet(BASE);
    return (await parseData<FirmSummary[]>(response)).map(withBranding);
  },

  /**
   * POST /firm/firms — create a practice, becoming its owner.
   *
   * Free and self-service (§5.4). No approval step, and no requirement to
   * have a client yet: §5.5.1 depends on an accountant who arrived through a
   * client's invitation being able to do this in the same sitting.
   */
  async createFirm(input: CreateFirmInput): Promise<CreatedFirm> {
    const response = await apiPost(BASE, input);
    const created = await parseData<CreatedFirm>(response);
    // The server always sends the list; defaulting here keeps the screen from
    // having to guard an array it renders three ways.
    return withBranding({ ...created, proposed_clients: created.proposed_clients ?? [] });
  },

  /**
   * PATCH /firm/settings — the practice's own details (§5.2).
   *
   * THE OWNER'S ALONE, enforced by the server. A field left out is untouched;
   * a field sent empty is CLEARED, which is the only way to remove a phone
   * number the practice no longer uses.
   *
   * No `:id` in the path on purpose: the firm is named by `X-Firm-Id` like
   * every other firm-scoped call, so there is no second way to say which firm
   * and no chance of the two disagreeing.
   */
  async updateFirmSettings(firmId: string, input: FirmSettingsInput): Promise<FirmSummary> {
    const response = await apiPatch('/api/v1/firm/settings', input, {
      headers: { 'X-Firm-Id': firmId },
    });
    return withBranding(await parseData<FirmSummary>(response));
  },

  /**
   * POST /firm/settings/logo — multipart, owner only. PNG or JPEG.
   *
   * Its own call rather than a field on the PATCH above: a file cannot ride on
   * a JSON body, the same split the business logo has had since PRD 29.
   */
  async uploadFirmLogo(firmId: string, file: File): Promise<FirmSummary> {
    const form = new FormData();
    form.append('logo', file);
    const response = await apiPostForm('/api/v1/firm/settings/logo', form, {
      headers: { 'X-Firm-Id': firmId },
    });
    return withBranding(await parseData<FirmSummary>(response));
  },

  /**
   * DELETE /firm/settings/logo — detaches. A proposal already sent keeps the
   * mark it was sent with.
   */
  async removeFirmLogo(firmId: string): Promise<FirmSummary> {
    const response = await apiDelete('/api/v1/firm/settings/logo', {
      headers: { 'X-Firm-Id': firmId },
    });
    return withBranding(await parseData<FirmSummary>(response));
  },

  /**
   * GET /firm/clients — the §5.6 worklist, firm-scoped.
   *
   * Sends `X-Firm-Id`, which is a DIFFERENT seam from `X-Business-Id` and is
   * deliberately not routed through the business helpers: a firm call that
   * accidentally sent the business header would be a cross-tenant read.
   *
   * Filters go on the query string rather than in a body, because §5.6 wants
   * them in the URL — a partner sends "these six are behind" as a link.
   */
  async listClients(
    firmId: string,
    filters: WorklistFilter[] = [],
    // A TAB KEY, not only a client type: `fiduciary` is the third tab and
    // holds `trust` and `estate` (PRD 79 §4.1). Nothing is stored as
    // "fiduciary" — the server expands it.
    clientType?: ClientType | 'fiduciary'
  ): Promise<WorklistResponse> {
    const parts = filters.map(f => `filters[]=${encodeURIComponent(f)}`);
    // The tabs are a filter on THIS response, never a second endpoint (PRD 70
    // §4.8) — two derivations of "how much work does this client need"
    // disagree within a quarter.
    if (clientType) parts.push(`type=${encodeURIComponent(clientType)}`);
    const query = parts.join('&');
    const response = await apiGet(`/api/v1/firm/clients${query ? `?${query}` : ''}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<WorklistResponse>(response);
  },

  /**
   * GET /firm/migrations — the §5.7.1 board.
   *
   * Readable by staff as well as partners, deliberately: on a QBO migration we
   * do the work WITH the firm, and a board only the partner can open is a
   * board the person doing the migration cannot use. Staff still see only
   * their assigned clients, which the server enforces.
   */
  async migrationBoard(firmId: string): Promise<MigrationBoard> {
    const response = await apiGet('/api/v1/firm/migrations', {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<MigrationBoard>(response);
  },

  /**
   * POST /firm/migrations/:businessId/review — accept the imported opening
   * balances.
   *
   * The one judgment on the board. Returns the row with its new status, which
   * may be `live` rather than `reviewed`: signing off on books somebody has
   * already been posting to means they are being kept, not merely accepted.
   */
  async reviewMigration(
    firmId: string,
    businessId: string,
    notes?: string
  ): Promise<MigrationRow> {
    const response = await apiPost(
      `/api/v1/firm/migrations/${businessId}/review`,
      notes ? { notes } : {},
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<MigrationRow>(response);
  },

  /**
   * DELETE /firm/migrations/:businessId/review — withdraw a sign-off.
   *
   * Because one can be given in error, and a board that cannot go backwards is
   * one people stop trusting.
   */
  async withdrawMigrationReview(firmId: string, businessId: string): Promise<MigrationRow> {
    const response = await apiDelete(`/api/v1/firm/migrations/${businessId}/review`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<MigrationRow>(response);
  },

  /**
   * PATCH /firm/migrations/:businessId/notes — the note, on its own.
   *
   * NOT a re-review. Posting a review again restamps `reviewed_at` and
   * `reviewed_by` with whoever is fixing the typo, which changes the answer to
   * "who accepted these opening balances" — the one fact this board is
   * accountable for. An empty string CLEARS the note.
   */
  async updateMigrationNotes(
    firmId: string,
    businessId: string,
    notes: string
  ): Promise<MigrationRow> {
    const response = await apiPatch(
      `/api/v1/firm/migrations/${businessId}/notes`,
      { notes },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<MigrationRow>(response);
  },

  /**
   * POST /firm/clients — propose an engagement (§5.7 step 1).
   *
   * Always an invitation, never a direct write, even for a firm that already
   * has a membership on the client's books: with `firm_billed_rebilled` the
   * default, a direct write would let a firm make itself the payer for
   * somebody else's subscription without being asked.
   */
  async inviteClient(
    firmId: string,
    input: { email: string; billingArrangement: BillingArrangement; firmClientId?: string }
  ): Promise<FirmEngagementInvitation> {
    const response = await apiPost(
      '/api/v1/firm/clients',
      {
        email: input.email,
        billing_arrangement: input.billingArrangement,
        // The CONVERSION case (PRD 70 §4.7): a client this firm already acts
        // for, with no books, taking some on. Acceptance updates that row, so
        // its id — and every work item and invoice hung off it — survives.
        ...(input.firmClientId ? { firm_client_id: input.firmClientId } : {}),
      },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmEngagementInvitation>(response);
  },

  /**
   * POST /firm/clients with `books: false` — record a client who has no books
   * (PRD 70 §4.3).
   *
   * THE ONE INTAKE PATH WITH NO INVITATION AND NO EMAIL, and not a shortcut
   * around the rule above. That rule exists because an engagement with an
   * entity group grants access to somebody else's books and decides who pays
   * for them. A client without books grants nothing and costs nothing, so
   * there is nobody to ask — and asking would mean mailing a 1040 client for
   * permission to write their name down.
   */
  async recordClient(firmId: string, input: RecordClientInput): Promise<RecordedClient> {
    const response = await apiPost(
      '/api/v1/firm/clients',
      {
        books: false,
        client_type: input.clientType,
        name: input.name,
        contact_name: input.contactName || undefined,
        email: input.email || undefined,
        phone: input.phone || undefined,
      },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<RecordedClient>(response);
  },

  /**
   * GET /firm/clients/:id — one client, with its open work (PRD 70 Wave 2).
   */
  async getClient(firmId: string, firmClientId: string): Promise<FirmClientDetail> {
    const response = await apiGet(`/api/v1/firm/clients/${firmClientId}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmClientDetail>(response);
  },

  /**
   * GET /firm/clients/linkable — people who have already shared a personal
   * account with this firm and are not yet on a client row (PRD 70 §4.6).
   *
   * NOT A USER SEARCH, and it cannot be turned into one: the only addresses it
   * can ever contain are ones somebody handed this firm by granting it access.
   */
  async listLinkable(firmId: string): Promise<LinkableUser[]> {
    const response = await apiGet('/api/v1/firm/clients/linkable', {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<LinkableUser[]>(response);
  },

  /**
   * POST …/contacts/:contactId/link — claim somebody who already shares.
   *
   * PER PERSON since PRD 79 §4.6: a Greenback account belongs to a person, and
   * a contact is a person. Both spouses on a joint return can be linked.
   */
  async linkClient(
    firmId: string,
    firmClientId: string,
    contactId: string,
    userId: number
  ): Promise<WorklistClient> {
    const response = await apiPost(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}/link`,
      { user_id: userId },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<WorklistClient>(response);
  },

  /**
   * POST …/contacts/:contactId/link/invite — ask that person to confirm.
   *
   * Says NOTHING about whether that address has a Greenback account. The screen
   * must not imply otherwise: the same answer comes back either way, and that
   * is the point rather than a limitation.
   */
  async inviteClientLink(
    firmId: string,
    firmClientId: string,
    contactId: string
  ): Promise<WorklistClient> {
    const response = await apiPost(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}/link/invite`,
      {},
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<WorklistClient>(response);
  },

  /** DELETE …/contacts/:contactId/link — undoing it takes nothing away either. */
  async unlinkClient(
    firmId: string,
    firmClientId: string,
    contactId: string
  ): Promise<WorklistClient> {
    const response = await apiDelete(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}/link`,
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<WorklistClient>(response);
  },

  // ---- Contacts (PRD 79 §4.2) ----------------------------------------------

  /** POST /firm/clients/:id/contacts — add a person to the file. */
  async addClientContact(
    firmId: string,
    firmClientId: string,
    input: ClientContactInput
  ): Promise<FirmClientDetail> {
    const response = await apiPost(`/api/v1/firm/clients/${firmClientId}/contacts`, input, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmClientDetail>(response);
  },

  async updateClientContact(
    firmId: string,
    firmClientId: string,
    contactId: string,
    input: ClientContactInput
  ): Promise<FirmClientDetail> {
    const response = await apiPatch(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}`,
      input,
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmClientDetail>(response);
  },

  /** The primary is the address the firm writes to, and there is exactly one. */
  async makeClientContactPrimary(
    firmId: string,
    firmClientId: string,
    contactId: string
  ): Promise<FirmClientDetail> {
    const response = await apiPost(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}/primary`,
      {},
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmClientDetail>(response);
  },

  async removeClientContact(
    firmId: string,
    firmClientId: string,
    contactId: string
  ): Promise<FirmClientDetail> {
    const response = await apiDelete(
      `/api/v1/firm/clients/${firmClientId}/contacts/${contactId}`,
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmClientDetail>(response);
  },

  /** PATCH /firm/clients/:id — the practice's own record of this client. */
  async updateClientRecord(
    firmId: string,
    firmClientId: string,
    input: Partial<RecordClientInput>
  ): Promise<WorklistClient> {
    const response = await apiPatch(
      `/api/v1/firm/clients/${firmClientId}`,
      {
        ...(input.name === undefined ? {} : { name: input.name }),
        ...(input.email === undefined ? {} : { email: input.email }),
        ...(input.phone === undefined ? {} : { phone: input.phone }),
        ...(input.clientType === undefined ? {} : { client_type: input.clientType }),
      },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<WorklistClient>(response);
  },

  /**
   * POST /firm/clients/businesses — set books up FOR a prospect (§5.7 step 1).
   *
   * One call, not two: creating a business on a client's behalf IS the offer,
   * so the server writes the books and the invitation in one transaction and
   * returns both. There is deliberately no "now send it" step for the firm to
   * forget.
   */
  async createClientBusiness(
    firmId: string,
    input: CreateClientBusinessInput
  ): Promise<CreatedClientBusiness> {
    const response = await apiPost(
      '/api/v1/firm/clients/businesses',
      {
        email: input.email,
        name: input.name,
        billing_arrangement: input.billingArrangement,
        entity_type: input.entityType || undefined,
        coa_template: input.coaTemplate || undefined,
        // See `inviteClient` — the same conversion, by the other intake path.
        ...(input.firmClientId ? { firm_client_id: input.firmClientId } : {}),
      },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<CreatedClientBusiness>(response);
  },

  /**
   * POST /firm/clients/:id/accept — the firm signing a CLIENT-proposed
   * engagement (§5.5.1).
   *
   * Access materialises here, not when the client asked: a client naming a
   * firm does not put that firm's staff into their books.
   */
  async acceptClientProposal(firmId: string, firmClientId: string): Promise<WorklistClient> {
    const response = await apiPost(`/api/v1/firm/clients/${firmClientId}/accept`, {}, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<WorklistClient>(response);
  },

  /** POST /firm/clients/:id/decline — the firm turning one down. */
  async declineClientProposal(firmId: string, firmClientId: string): Promise<WorklistClient> {
    const response = await apiPost(`/api/v1/firm/clients/${firmClientId}/decline`, {}, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<WorklistClient>(response);
  },

  /**
   * GET /firm/clients/:id/coverage — the §5.8.1 completeness check.
   *
   * Needs no grant from anybody: every signal is already in the client's own
   * books, so it works the day a firm onboards them.
   */
  async coverage(
    firmId: string,
    clientId: string,
    window?: CoverageWindow
  ): Promise<ClientCoverage> {
    const query = window ? `?year=${window.year}&start_month=${window.startMonth}` : '';
    const response = await apiGet(`/api/v1/firm/clients/${clientId}/coverage${query}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<ClientCoverage>(response);
  },

  /**
   * GET /firm/clients/:id/coverage/workpaper — the same facts as a PDF.
   *
   * Rendered on demand rather than stored: a workpaper is what we could see
   * when the accountant asked, so asking again after chasing a stalled feed
   * should produce a different and better document.
   */
  async downloadWorkpaper(
    firmId: string,
    clientId: string,
    window?: CoverageWindow
  ): Promise<DownloadedFile> {
    const query = window ? `?year=${window.year}&start_month=${window.startMonth}` : '';
    const response = await apiGet(
      `/api/v1/firm/clients/${clientId}/coverage/workpaper${query}`,
      { headers: { 'X-Firm-Id': firmId } }
    );
    if (!response.ok) {
      await parseData(response); // throws, carrying the server's own message
      throw new BusinessApiError(response.status, 'http_error', 'Could not build the workpaper');
    }
    return {
      blob: await response.blob(),
      filename: dispositionFilename(response) ?? 'coverage-workpaper.pdf',
    };
  },

  /**
   * POST /firm/clients/invitations/:id/resend — ask a prospective client again.
   *
   * Resends a LAPSED invitation as readily as a live one: expiry is a closed
   * window, not a terminal state, and recovering from it is the point. The
   * server refuses a declined or revoked one, and enforces the send cap and
   * cooldown — this sends no opinion about either, so the message the user sees
   * is the server's.
   */
  async resendClientInvitation(firmId: string, invitationId: string): Promise<FirmEngagementInvitation> {
    const response = await apiPost(
      `/api/v1/firm/clients/invitations/${invitationId}/resend`,
      {},
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmEngagementInvitation>(response);
  },

  /** GET /firm/staff — the roster plus invitations nobody has accepted yet. */
  async listStaff(firmId: string): Promise<FirmStaffResponse> {
    const response = await apiGet('/api/v1/firm/staff', {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffResponse>(response);
  },

  /**
   * PATCH /firm/staff/:id — what somebody IS in the practice.
   *
   * Exists so a promotion is not a revoke-and-reinvite round trip: that would
   * cascade their client access away, send a second email, and require them to
   * accept again in order to change a title. Ownership is the owner's alone to
   * give or take, which the server enforces.
   */
  async updateStaffRole(firmId: string, membershipId: string, role: FirmRole): Promise<FirmStaffMember> {
    const response = await apiPatch(`/api/v1/firm/staff/${membershipId}`, { role }, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffMember>(response);
  },

  /**
   * POST /firm/staff/:id/reinstate — somebody back from a revocation.
   *
   * Restores exactly the clients they were assigned to, because revoking
   * removes the client-side MEMBERSHIPS and never the roster. Coming back from
   * leave is therefore one click rather than forty.
   */
  async reinstateStaff(firmId: string, membershipId: string): Promise<FirmStaffMember> {
    const response = await apiPost(`/api/v1/firm/staff/${membershipId}/reinstate`, {}, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffMember>(response);
  },

  /**
   * POST /firm/staff/:id/sign-out — end every session this person holds.
   *
   * THE ANSWER TO "RESET THEIR PASSWORD", which cannot be done because there
   * are no passwords. It reaches their WHOLE account rather than this firm's
   * clients alone — a session is a session — and the screen says so before
   * anybody presses it.
   */
  async signOutStaff(firmId: string, membershipId: string): Promise<FirmStaffMember> {
    const response = await apiPost(`/api/v1/firm/staff/${membershipId}/sign-out`, {}, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffMember>(response);
  },

  /**
   * GET /firm/staff/:id/assignments — what this person reaches, beside what
   * they could.
   *
   * Every engagement the firm holds is listed, not only the ones they are on:
   * this is the screen that changes the answer, and a picker showing only the
   * current answer cannot.
   */
  async staffAssignments(firmId: string, membershipId: string): Promise<FirmStaffAssignments> {
    const response = await apiGet(`/api/v1/firm/staff/${membershipId}/assignments`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffAssignments>(response);
  },

  /**
   * PUT /firm/staff/:id/assignments — set what they reach, declaratively.
   *
   * The request STATES who serves what; the server adds and removes rows to
   * match. An engagement left out of `assignments` is one they no longer serve.
   * `own_books_role` is different: absent leaves it alone, and an explicit null
   * takes it away — so a screen editing only one half must send only that half.
   */
  async updateStaffAssignments(
    firmId: string,
    membershipId: string,
    input: FirmStaffAccessInput
  ): Promise<FirmStaffAssignments> {
    const response = await apiPut(`/api/v1/firm/staff/${membershipId}/assignments`, input, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffAssignments>(response);
  },

  /** POST /firm/staff/invitations/:id/resend — ask somebody again. */
  async resendStaffInvitation(firmId: string, invitationId: string): Promise<FirmStaffInvitation> {
    const response = await apiPost(`/api/v1/firm/staff/invitations/${invitationId}/resend`, {}, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffInvitation>(response);
  },

  /**
   * DELETE /firm/staff/invitations/:id — withdraw one nobody accepted.
   *
   * The row is REVOKED, not deleted: "they turned us down" and "we changed our
   * mind" have to stay distinguishable, so the server returns the updated row
   * rather than nothing.
   */
  async cancelStaffInvitation(firmId: string, invitationId: string): Promise<FirmStaffInvitation> {
    const response = await apiDelete(`/api/v1/firm/staff/invitations/${invitationId}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffInvitation>(response);
  },

  /**
   * POST /firm/staff — invite somebody to the practice by email.
   *
   * Keyed by address rather than by user: a practice hiring a bookkeeper
   * should not have to know whether that person has a Greenback account yet.
   * An invitation may not make somebody an OWNER — that is transferred
   * deliberately by an existing owner, never handed to an unclaimed address.
   */
  async inviteStaff(
    firmId: string,
    input: { email: string; role: FirmRole }
  ): Promise<FirmStaffInvitation> {
    const response = await apiPost('/api/v1/firm/staff', input, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmStaffInvitation>(response);
  },

  /** DELETE /firm/staff/:id — one action, cascading to every client (§3.2). */
  async revokeStaff(firmId: string, membershipId: string): Promise<void> {
    const response = await apiDelete(`/api/v1/firm/staff/${membershipId}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    await parseData(response);
  },

  /**
   * GET /firm/staff/:id/access — what this person had access to (§3.2).
   *
   * Reconstructed from audit rows, so it keeps answering after the
   * memberships have been destroyed.
   */
  async staffAccessReport(firmId: string, membershipId: string): Promise<StaffAccessReport> {
    const response = await apiGet(`/api/v1/firm/staff/${membershipId}/access`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<StaffAccessReport>(response);
  },

  /**
   * GET /firm/billing — what the practice pays for, and why (§5.4.2).
   *
   * Readable by the whole firm on purpose: "how many seats are we paying for"
   * is not privileged inside a practice, and a staff member who can see a
   * client is read-only should be able to see whether the firm's own card is
   * the cause. Spending is gated separately, by `can_check_out`.
   */
  async getBilling(firmId: string): Promise<FirmBillingResponse> {
    const response = await apiGet('/api/v1/firm/billing', {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmBillingResponse>(response);
  },

  /**
   * POST /firm/billing/checkout — returns a URL to redirect to.
   *
   * Creates nothing here and nothing in our database: the subscription becomes
   * real when Stripe says so, through the webhook.
   */
  async startCheckout(
    firmId: string,
    params: { successUrl: string; cancelUrl: string }
  ): Promise<string> {
    const response = await apiPost(
      '/api/v1/firm/billing/checkout',
      { success_url: params.successUrl, cancel_url: params.cancelUrl },
      { headers: { 'X-Firm-Id': firmId } }
    );
    const { url } = await parseData<{ url: string }>(response);
    return url;
  },

  /**
   * POST /firm/billing/portal — Stripe's own card, invoice, plan and
   * cancellation screens.
   *
   * A firm in grace needs to fix the card on an EXISTING subscription, which a
   * fresh Checkout session does not do — it would start a second one.
   *
   * `flow` deep-links to ONE of those tasks. Omit it for the portal's home
   * page, which is where the invoice history lives — Stripe has no invoice
   * flow, because the history is the home page.
   *
   * The subscription a flow acts on is NOT sent: the server reads it from the
   * firm's own entitlement, so there is no subscription id in a request body
   * for anybody to substitute.
   */
  async openBillingPortal(
    firmId: string,
    returnUrl: string,
    flow?: BillingPortalFlow
  ): Promise<string> {
    const response = await apiPost(
      '/api/v1/firm/billing/portal',
      { return_url: returnUrl, ...(flow ? { flow } : {}) },
      { headers: { 'X-Firm-Id': firmId } }
    );
    const { url } = await parseData<{ url: string }>(response);
    return url;
  },

  /**
   * POST /firm/clients/:id/billing-transfer — ask the client to change who
   * pays (PRD 40 §5.5.3).
   *
   * A PROPOSAL, never a write. "A firm cannot bill a client that has not
   * consented" cuts both ways: handing a bill BACK is equally a change to
   * somebody's money, so it waits on their answer too. Fourteen days, then it
   * lapses.
   *
   * The response carries `effect` — what this would mean for the client's
   * access, read from the entitlement seam rather than inferred — which is
   * the half of §5.5.3 that says "both parties are told why".
   */
  async proposeBillingTransfer(
    firmId: string,
    engagementId: string,
    toArrangement: BillingArrangement,
    note?: string
  ): Promise<FirmBillingTransfer> {
    const response = await apiPost(
      `/api/v1/firm/clients/${engagementId}/billing-transfer`,
      { to_arrangement: toArrangement, ...(note ? { note } : {}) },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmBillingTransfer>(response);
  },

  /**
   * POST /firm/clients/:id/billing-transfer/respond
   *
   * THE BODY PARAM IS `decision`, NEVER `action`. `params[:action]` is Rails'
   * own controller-action name, so a body param called `action` is silently
   * unreadable and every response 422s with "unknown action" — the client
   * half of this pair carries the same warning, having been bitten by it.
   *
   * Which verb is allowed is the server's call, not this screen's: only the
   * proposer may withdraw, and only the other side may accept or decline.
   */
  async respondToBillingTransfer(
    firmId: string,
    engagementId: string,
    decision: FirmBillingTransferDecision
  ): Promise<FirmBillingTransfer> {
    const response = await apiPost(
      `/api/v1/firm/clients/${engagementId}/billing-transfer/respond`,
      { decision },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmBillingTransfer>(response);
  },

  /**
   * The modules a firm pays for on one client, or turns on for its own books
   * when `engagementId` is null — free (PRD 112 §4.6.3).
   */
  async getClientModules(firmId: string, engagementId: string | null): Promise<FirmClientModulesResponse> {
    const response = await apiGet(clientModulesPath(engagementId), { headers: { 'X-Firm-Id': firmId } });
    return parseData<FirmClientModulesResponse>(response);
  },

  /** What adding `key` costs the firm a month, in cents (0 on own books). */
  async previewClientModule(firmId: string, engagementId: string | null, key: string): Promise<number | null> {
    const response = await apiGet(`${clientModulesPath(engagementId)}/preview?key=${encodeURIComponent(key)}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    const { monthly_increase_cents: cents } = await parseData<{ monthly_increase_cents: number | null }>(response);
    return cents;
  },

  /**
   * POST — the owner adds a module. `monthlyIncreaseCents` is the figure the
   * owner was shown; a stale one is refused 409 `terms_changed` with the new
   * figure, so nobody is billed a number they did not see.
   */
  async addClientModule(
    firmId: string,
    engagementId: string | null,
    key: string,
    monthlyIncreaseCents: number | null
  ): Promise<FirmClientModulesResponse> {
    const response = await apiPost(
      clientModulesPath(engagementId),
      { key, ...(monthlyIncreaseCents === null ? {} : { monthly_increase_cents: monthlyIncreaseCents }) },
      { headers: { 'X-Firm-Id': firmId } }
    );
    return parseData<FirmClientModulesResponse>(response);
  },

  /** DELETE — ends it now; the firm's card is credited the rest of the month. */
  async removeClientModule(firmId: string, engagementId: string | null, key: string): Promise<FirmClientModulesResponse> {
    const response = await apiDelete(`${clientModulesPath(engagementId)}/${encodeURIComponent(key)}`, {
      headers: { 'X-Firm-Id': firmId },
    });
    return parseData<FirmClientModulesResponse>(response);
  },

  /** GET /firm/staff/:id/access/export — the same report as a file. */
  async downloadStaffAccessReport(
    firmId: string,
    membershipId: string
  ): Promise<DownloadedFile> {
    const response = await apiGet(`/api/v1/firm/staff/${membershipId}/access/export`, {
      headers: { 'X-Firm-Id': firmId },
    });
    if (!response.ok) {
      await parseData(response); // throws, carrying the server's own message
      throw new BusinessApiError(response.status, 'http_error', 'Could not generate the export');
    }
    return {
      blob: await response.blob(),
      filename: dispositionFilename(response) ?? 'staff-access-report.csv',
    };
  },
};

/** A client's modules, or the practice's own books' when there is no engagement. */
function clientModulesPath(engagementId: string | null): string {
  return engagementId ? `/api/v1/firm/clients/${engagementId}/modules` : '/api/v1/firm/own-books/modules';
}
